// QuoteModule — MIDNIGHT elevation
//
// Quote section inside DetailDrawer.
// §10 glass nesting: ModulePanel = ds-tile-inset (flat rgba, no backdrop-filter).
// Header: SectionHeader with peach (quote phase) icon chip; Edit/Send/PDF via Button variant="icon".
// The PDF button previews the quote in a sheet with Download (UX-085).
// All existing data logic (line items, tax display, real-vs-computed tax) preserved.

import { FileText } from 'lucide-react'
import { formatCurrency } from '@/utils/formatting'
import { formatQty } from '@/lib/format'
import type { Record_ } from './types'
import { SectionHeader, ModulePanel } from './_drawerHelpers'
import { QuoteIconActions } from './DrawerHeaderActions'

interface QuoteModuleProps {
  record: Record_
  onResendQuote?: () => void
  onEditQuote?: () => void
  /** False on a phone sheet: the same buttons render in the drawer header. */
  inlineActions?: boolean
}

export function QuoteModule({ record, onResendQuote, onEditQuote, inlineActions = true }: QuoteModuleProps) {

  const items = record.lineItems ?? []
  const total = record.total

  // Bug J: use real subtotal/tax from Record_ (populated from ApiQuote.subtotal / ApiQuote.tax)
  // rather than back-computing with a hardcoded 8.75% assumption.
  const hasRealTax = record.quoteSubtotal != null && record.quoteTax != null
  const subtotal   = hasRealTax ? record.quoteSubtotal! : undefined
  const tax        = hasRealTax ? record.quoteTax!        : undefined
  const taxLabel   =
    record.quoteTaxRate != null
      ? `Tax (${
          record.quoteTaxRate % 1 === 0
            ? record.quoteTaxRate.toFixed(0)
            : record.quoteTaxRate.toFixed(2)
        }%)`
      : 'Tax'

  // Optional add-ons are offered, not in the total, so they never pad the
  // "+N more" count shown right above that total (shard-1).
  const included = items.filter((li) => !li.optional)
  const optionalCount = items.length - included.length
  const showAll = included.length <= 4
  const visible = showAll ? included : included.slice(0, 3)
  const hidden  = showAll ? 0 : included.length - 3

  return (
    <ModulePanel accent="peach">
      {/* Header row: SectionHeader + icon action buttons */}
      <div className="ds-doc-head">
        <SectionHeader
          tone="peach"
          icon={FileText}
          title="Quote"
          meta={
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {record.quoteNumber ?? 'Draft quote'}
              {record.quoteSentAt ? ` · sent ${record.quoteSentAt}` : ' · not sent yet'}
            </span>
          }
        />
        {inlineActions && (
          <QuoteIconActions record={record} onResendQuote={onResendQuote} onEditQuote={onEditQuote} />
        )}
      </div>

      {/* Line items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {visible.map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              gap: 8,
              fontSize: 13,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ color: 'rgb(var(--fg-rgb) / 0.80)' }}>{item.desc}</span>
              <span style={{ color: 'var(--midnight-text3)' }}>
                {' '}· {formatQty(item.qty, item.unit)}
              </span>
            </div>
            <span
              style={{
                color: 'rgb(var(--fg-rgb) / 0.60)',
                fontVariantNumeric: 'tabular-nums',
                flexShrink: 0,
              }}
            >
              {formatCurrency(item.qty * item.price)}
            </span>
          </div>
        ))}
        {hidden > 0 && (
          <p
            style={{
              fontSize: 12,
              color: 'var(--midnight-text3)',
              paddingTop: 4,
            }}
          >
            +{hidden} more line {hidden === 1 ? 'item' : 'items'}
          </p>
        )}
        {optionalCount > 0 && (
          <p data-testid="quote-optional-count" style={{ fontSize: 12, color: 'var(--midnight-text3)', paddingTop: hidden > 0 ? 0 : 4 }}>
            {optionalCount} optional {optionalCount === 1 ? 'add-on' : 'add-ons'}, not in the total
          </p>
        )}
      </div>

      {/* Totals */}
      <div
        style={{
          marginTop: 12,
          paddingTop: 12,
          borderTop: '0.5px solid rgb(var(--fg-rgb) / 0.06)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {hasRealTax ? (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                fontSize: 13,
              }}
            >
              <span style={{ color: 'var(--midnight-text3)' }}>Subtotal</span>
              <span
                style={{
                  color: 'rgb(var(--fg-rgb) / 0.60)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatCurrency(subtotal!)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                fontSize: 13,
              }}
            >
              <span style={{ color: 'var(--midnight-text3)' }}>{taxLabel}</span>
              <span
                style={{
                  color: 'rgb(var(--fg-rgb) / 0.60)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatCurrency(tax!)}
              </span>
            </div>
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              fontSize: 13,
            }}
          >
            <span style={{ color: 'var(--midnight-text3)' }}>Subtotal</span>
            <span
              style={{
                color: 'rgb(var(--fg-rgb) / 0.60)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatCurrency(total)}
            </span>
          </div>
        )}

        {/* Grand total */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            paddingTop: 6,
          }}
        >
          <span
            style={{ fontSize: 15, fontWeight: 600, color: 'rgb(var(--fg-rgb) / 0.80)' }}
          >
            Total
          </span>
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: 'rgb(var(--fg-rgb))',
            }}
          >
            {formatCurrency(total)}
          </span>
        </div>
      </div>
    </ModulePanel>
  )
}
